// How the ads did › Suggested changes, Blueprint only (pages/ux-full screens/ads.js, "Suggested ad
// changes"): the queue from adChanges.list (a RowList), each change opened in a SidePanel to approve, or
// dismiss with why not (adChanges.decide), then marked done once someone has made it in the platform
// (adChanges.markDone). Flowology writes nothing to Google Ads, Microsoft Ads or Local Services: an
// approved change is one to make there by hand. The weekly review runs from the worker; "Run the review
// now" runs it on demand. A chat draws a change with ui-web's AdChange view instead, and never decides.
import { useState, type ReactNode } from 'react';
import { AdChangesProps, channelWord, changeState, type AdChangeRow } from '@flowology/ui-web';
import {
  KeyValue,
  Label,
  Link,
  RowList,
  SidePanel,
  TextField,
  adChangeDecided,
  adChangeFacts,
  adChangeFigures,
  adChangeRows,
} from '@flowology/ui-web/fl';
import type { DashboardSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { CLOSED_CHANGES, mayDecideAdChanges, OPEN_CHANGES, reviewWords } from './ads-model.ts';
import { answered } from './dash-sections.ts';

type List = { changes: AdChangeRow[]; next: string | null; waiting: number };
type Key = { key: string; renew: () => void };

const EMPTY =
  'Nothing waits. The weekly ads review adds what it finds here each Monday; so can a person, or an AI tool they’ve connected. Each waits for someone here to decide it.';

/** One change in its panel: where it stands, why, what changes, its figures, what was decided, and the
 * decision for whoever makes it (approve, or dismiss saying why not; then mark it done). It stays open
 * once decided, showing the change as it now stands, as the sheet before it did. */
function ChangePanel({
  change: c,
  timeZone,
  decides,
  busy,
  error,
  onClose,
  act,
}: {
  change: AdChangeRow;
  timeZone: string;
  decides: boolean;
  busy: boolean;
  /** Why the last decision didn't go through. */
  error: string | null;
  onClose: () => void;
  act: (action: 'approve' | 'dismiss' | 'done', note?: string) => void;
}) {
  const [dismissing, setDismissing] = useState(false);
  const [why, setWhy] = useState('');
  const [askedWhy, setAskedWhy] = useState(false);
  const where = channelWord(c.platform);
  const [word, kind] = changeState(c);
  const decided = adChangeDecided(c, timeZone);
  const figures = adChangeFigures(c);
  const waits = c.status === 'waiting';
  const doing = decides && (waits || c.status === 'approved');
  const primary = !doing
    ? null
    : busy
      ? { label: 'Saving…' }
      : !waits
        ? { label: `Mark done: made in ${where}`, onPress: () => act('done') }
        : dismissing
          ? {
              label: 'Dismiss it',
              onPress: () => (why.trim() ? act('dismiss', why.trim()) : setAskedWhy(true)),
            }
          : { label: `Approve: to do in ${where}`, onPress: () => act('approve') };
  const links =
    doing && waits && !busy
      ? [
          dismissing
            ? { label: 'Keep it waiting', onPress: () => setDismissing(false) }
            : { label: 'Dismiss…', onPress: () => setDismissing(true) },
        ]
      : [];
  return (
    <SidePanel
      label={c.title}
      title={c.title}
      sub={`${where}, ${c.target}`}
      note={error ?? (askedWhy && !why.trim() ? 'Say why not first.' : null)}
      primary={primary}
      links={links}
      onClose={onClose}
      takesFocus
    >
      <p className="bp-rep-sub">
        <Label kind={kind}>{word}</Label>
      </p>
      <KeyValue items={adChangeFacts(c, timeZone)} label="The change" />
      {figures.length > 0 && <KeyValue items={figures} layout="rows" label="Its figures" />}
      {decided.map((line, i) => (
        <p key={i} className={i ? 'bp-rep-sub' : 'bp-rep-said'} role={i ? undefined : 'status'}>
          {line}
        </p>
      ))}
      {doing && waits && dismissing && (
        <TextField
          label="Why not? It’s kept, so the review doesn’t suggest it again soon"
          value={why}
          maxLength={500}
          autoFocus
          onChange={setWhy}
        />
      )}
      {!doing && waits && <p className="bp-rep-sub">Decided by the owner or the manager.</p>}
      {doing && waits && !dismissing && (
        <p className="bp-rep-sub">
          Approving puts it on the list to make in {where} by hand. Flowology doesn’t change the ad account.
        </p>
      )}
    </SidePanel>
  );
}

/** Suggested changes as a dashboard section, and the panel of the one that's open (the page's layer). */
export function useAdChanges({ timeZone, held }: { timeZone: string; held: readonly string[] }): {
  section: DashboardSection;
  sheet: ReactNode;
} {
  const [closed, setClosed] = useState(false);
  const q = useQuery<List>('adChanges.list', { status: closed ? CLOSED_CHANGES : OPEN_CHANGES, limit: 50 });
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  // the change as the last decision left it, and why one didn't go through
  const [lastAnswer, setLastAnswer] = useState<AdChangeRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const decides = mayDecideAdChanges(held);
  // one key per attempt, so pressing again after a lost answer decides it once
  const approveKey = useSaveKey(`approve:${openId ?? ''}`);
  const dismissKey = useSaveKey(`dismiss:${openId ?? ''}`);
  const doneKey = useSaveKey(`done:${openId ?? ''}`);
  const reviewKey = useSaveKey('review');
  const shown =
    lastAnswer?.id === openId ? lastAnswer : (q.data?.changes.find((c) => c.id === openId) ?? null);
  const call = async (action: string, input: object, key: Key) => {
    setBusy(true);
    setError(null);
    try {
      const out = await client.call<AdChangeRow>(action, input, { idempotencyKey: key.key });
      key.renew();
      setLastAnswer(out);
      await q.reload();
    } catch (e) {
      setError(errorText(e, 'That didn’t go through. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  const act = (action: 'approve' | 'dismiss' | 'done', note?: string) =>
    void (action === 'done'
      ? call('adChanges.markDone', { id: openId }, doneKey)
      : call(
          'adChanges.decide',
          { id: openId, decision: action, ...(note ? { note } : {}) },
          action === 'approve' ? approveKey : dismissKey,
        ));
  const close = () => {
    setOpenId(null);
    setLastAnswer(null);
    setError(null);
  };
  const review = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await client.call<{ found: number; added: number; refreshed: number; quiet: number }>(
        'adChanges.review',
        {},
        { idempotencyKey: reviewKey.key },
      );
      reviewKey.renew();
      setSaid(reviewWords(r));
      if (closed) setClosed(false);
      else await q.reload();
    } catch (e) {
      setSaid(errorText(e, 'The review didn’t run. Try again.'));
    } finally {
      setBusy(false);
    }
  };
  const links = (
    <>
      <p className="bp-rep-more">
        {decides && (
          <Link onPress={() => void review()} isDisabled={busy}>
            {busy && !openId ? 'Running the review…' : 'Run the review now'}
          </Link>
        )}
        <Link onPress={() => setClosed((c) => !c)}>
          {closed ? 'Show what waits' : 'Show the dismissed and done'}
        </Link>
      </p>
      {said && (
        <p className="bp-rep-sub" role="status">
          {said}
        </p>
      )}
    </>
  );
  const section = answered(
    q,
    { title: 'Suggested changes', what: 'the suggested changes' },
    (d: List) => AdChangesProps.safeParse({ timeZone, changes: d.changes, waiting: d.waiting }),
    (p) => ({
      count: p.waiting,
      children: (
        <>
          <RowList
            rows={adChangeRows(p.changes, timeZone, setOpenId)}
            first={5}
            label="Suggested changes"
            empty={closed ? 'Nothing dismissed or done yet.' : EMPTY}
          />
          {links}
        </>
      ),
    }),
  );
  return {
    section,
    sheet: shown ? (
      <ChangePanel
        key={`${shown.id}:${shown.status}`}
        change={shown}
        timeZone={timeZone}
        decides={decides}
        busy={busy}
        error={error}
        onClose={close}
        act={act}
      />
    ) : null,
  };
}
